import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MailIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code. The widget will
// not render and handleSubmit will skip the verifyTurnstile call.
const ENABLE_FORGOT_PASSWORD_CAPTCHA = true;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
// The SECRET key lives in Firebase Secret Manager (TURNSTILE_SECRET_KEY)
// and is only readable by the verifyTurnstile Cloud Function.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    resetPassword
  } = useAuth();
  // ─── Role awareness ────────────────────────────────────────────────────
  // The standalone /forgot-password page is only linked to from teacher
  // entry points (Teacher Login page + Teacher Login Modal), so we default
  // to 'teacher' when no ?role= is supplied. Honoring an explicit ?role=
  // value keeps the page flexible if it is ever linked to from a student
  // surface in the future.
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const role: 'student' | 'teacher' = roleParam === 'student' ? 'student' : 'teacher';
  const signInPath = role === 'student' ? '/student-login' : '/teacher-login';
  // ─── Turnstile state ───────────────────────────────────────────────────
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  // ─── Load Turnstile script once ────────────────────────────────────────
  useEffect(() => {
    if (!ENABLE_FORGOT_PASSWORD_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget into our container ────────────────────
  useEffect(() => {
    if (!ENABLE_FORGOT_PASSWORD_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true;
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    // ─── Cloudflare Turnstile gate ────────────────────────────────────
    // Verify BEFORE we call sendPasswordResetEmail. If verification fails
    // the request is aborted and no reset email is sent.
    if (ENABLE_FORGOT_PASSWORD_CAPTCHA) {
      if (!turnstileToken) {
        setError('Please complete the verification challenge before submitting.');
        return;
      }
      setIsLoading(true);
      const honeypotEl = document.getElementById('mc-fp-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setError('Verification could not be completed. Please refresh the challenge and try again.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsLoading(false);
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setError('Verification failed. Please complete the challenge again and resubmit.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsLoading(false);
        return;
      }
    } else {
      setIsLoading(true);
    }
    try {
      await resetPassword(email, role);
      setMessage('Check your email for password reset instructions');
      // Reset the widget so a fresh challenge is required for any
      // subsequent request from the same page session.
      if (ENABLE_FORGOT_PASSWORD_CAPTCHA && turnstileWidgetIdRef.current && window.turnstile?.reset) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken(null);
    } catch (err: any) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
      if (ENABLE_FORGOT_PASSWORD_CAPTCHA && turnstileWidgetIdRef.current && window.turnstile?.reset) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  };
  const submitDisabled = isLoading || ENABLE_FORGOT_PASSWORD_CAPTCHA && !turnstileToken;
  return <div className="min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] flex items-center justify-center px-4 py-12" data-id="element-4374">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden" data-id="element-4375">
        <div className="px-8 py-10" data-id="element-4376">
          <div className="text-center mb-10" data-id="element-4377">
            <h2 className="text-3xl font-bold text-navy-900 mb-2" data-id="element-4378">
              Reset Password
            </h2>
            <p className="text-gray-600" data-id="element-4379">
              Enter your email to receive a password reset link
            </p>
          </div>
          {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start" data-id="element-4380">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-4381" />
              <span data-id="element-4382">{error}</span>
            </div>}
          {message && <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-start" data-id="element-4383">
              <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-4384" />
              <span data-id="element-4385">{message}</span>
            </div>}
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4386">
            <div data-id="element-4387">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4388">
                Email Address
              </label>
              <div className="relative" data-id="element-4389">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4390">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-4391" />
                </div>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isLoading} data-id="element-4392" />
              </div>
            </div>

            {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
            {ENABLE_FORGOT_PASSWORD_CAPTCHA && <div aria-hidden="true" style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }} data-id="element-4393">
                <label htmlFor="mc-fp-website-hp" data-id="element-4394">Website (leave blank)</label>
                <input id="mc-fp-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-4395" />
              </div>}

            {/* ─── Cloudflare Turnstile widget ─── */}
            {ENABLE_FORGOT_PASSWORD_CAPTCHA && <div data-id="element-4396">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4397">
                  Human Verification
                </label>
                <div className="flex justify-center" data-id="element-4398">
                  <div ref={turnstileContainerRef} data-id="element-4399" />
                </div>
              </div>}

            <button type="submit" disabled={submitDisabled} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4400">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4401">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4402"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4403"></path>
                  </svg>
                  Sending...
                </> : 'Send Reset Link'}
            </button>
          </form>
          <div className="mt-8 text-center" data-id="element-4404">
            <p className="text-gray-600" data-id="element-4405">
              Remember your password?{' '}
              <Link to={signInPath} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4406">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
};